import { createFileRoute } from "@tanstack/react-router";
import { seo } from "@utils/seo";
import i18n from "@modules/shared/localization/i18n";
import {
  productByIdQueryOptions,
  productQueryOptions,
  productRecommendationsQueryOptions,
} from "@modules/shopify/hooks";
import { getCountryVariables } from "@/modules/shared/localization/const/countries";
import { SingleProductPageView } from "@modules/shop/components/SingleProduct/SingleProductPageView";

const HEEL_INSOLE_PRODUCT_GID = "gid://shopify/Product/15616740622719";

export const Route = createFileRoute(
  "/$locale/_layout/(public)/_public/product/$handle"
)({
  component: SingleProductPage,
  ssr: true,
  loader: async ({ context: { queryClient }, params }) => {
    const localeVars = getCountryVariables(params.locale);
    const [productData, heelInsoleAddonProductData] = await Promise.all([
      queryClient.ensureQueryData(
        productQueryOptions({
          handle: params.handle,
          ...localeVars,
        })
      ),
      queryClient.ensureQueryData(
        productByIdQueryOptions({
          id: HEEL_INSOLE_PRODUCT_GID,
          ...localeVars,
        })
      ),
    ]);

    if (productData.product?.id) {
      await queryClient.prefetchQuery(
        productRecommendationsQueryOptions({
          productId: productData.product.id,
          ...localeVars,
        })
      );
    }

    return {
      ...productData,
      heelInsoleAddonProduct: heelInsoleAddonProductData.product ?? null,
    };
  },
  staleTime: 60_000,
  headers: () => ({
    "Cache-Control":
      "public, max-age=300, s-maxage=300, stale-while-revalidate=86400",
  }),
  head: ({ params, loaderData }) => {
    const t = i18n.getFixedT(params.locale);
    const product = loaderData?.product;
    const productName = product?.title ?? params.handle;
    return {
      meta: [
        ...seo({
          title:
            product?.seo.title || t("Seo:product.title", { name: productName }),
          description:
            product?.seo.description ||
            t("Seo:product.description", { name: productName }),
          image: product?.featuredImage?.url,
          type: "product",
        }),
      ],
    };
  },
});

function SingleProductPage() {
  const { handle, locale } = Route.useParams();
  const { heelInsoleAddonProduct } = Route.useLoaderData();

  return (
    <SingleProductPageView
      handle={handle}
      locale={locale}
      heelInsoleAddonProduct={heelInsoleAddonProduct}
    />
  );
}
